import { useMemo, useState, type KeyboardEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { capitalDesignTokens, circularProgressSizeProps } from '@opengov/capital-mui-theme';
import { ModalComposable } from '@opengov/components-modal';
import { arrayMove } from '@dnd-kit/sortable';
import { getIconComponent } from '../../utils/icons';
import { TestIds } from '../../constants';
import { useFavorites } from '../../contexts/FavoritesContext';
import { FAVORITES_CAP, type Favorite } from '../../services/favoritesStorage';
import { SortableItem, SortableList } from './SortableList';
import { FavoriteManagerRow } from './FavoriteManagerRow';

const CloseIcon = getIconComponent('Close');
const StarOutlineIcon = getIconComponent('StarOutline');

interface FavoritesManagerModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The Favorites Manager — the single surface for managing favorites. It edits a *local draft*
 * of the list (reorder, rename, remove, add the current page); nothing is persisted until the
 * user clicks Save Changes, and Cancel/Close/backdrop discards. Because the provider mounts
 * this on open, every open re-initializes the draft from the stored list and snapshots the
 * current page, so no effects are needed to keep things fresh.
 *
 * Built on the Capital Design System `ModalComposable` so the dialog chrome matches the shell.
 */
export function FavoritesManagerModal({ open, onClose }: FavoritesManagerModalProps) {
  const { favorites, replaceAll, managerStartInAdd, isSaving, isSaveError, clearSaveError } =
    useFavorites();

  // Snapshot the current page once, at mount (the provider mounts this on open).
  const [page] = useState(() => ({
    pathname: window.location.pathname,
    url: `${window.location.pathname}${window.location.search}`,
  }));

  // The working copy. All edits land here; Save commits it, Cancel/Close throws it away.
  const [draft, setDraft] = useState<Favorite[]>(() => favorites);
  const [isAdding, setIsAdding] = useState(() => managerStartInAdd);
  const [label, setLabel] = useState(() => document.title);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(favorites),
    [draft, favorites]
  );
  const atCap = draft.length >= FAVORITES_CAP;
  const alreadySaved = draft.some((favorite) => favorite.pathname === page.pathname);
  const trimmed = label.trim();
  const canAdd = !atCap && !alreadySaved && trimmed.length > 0;
  const destinationUrl = `${window.location.origin}${page.url}`;
  const ids = draft.map((favorite) => favorite.pathname);

  const handleRename = (pathname: string, nextLabel: string) => {
    const next = nextLabel.trim();
    if (!next) return; // blank → no-op
    clearSaveError();
    setDraft((prev) =>
      prev.map((favorite) =>
        favorite.pathname === pathname ? { ...favorite, label: next } : favorite
      )
    );
  };

  const handleRemove = (pathname: string) => {
    clearSaveError();
    setDraft((prev) => prev.filter((favorite) => favorite.pathname !== pathname));
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    clearSaveError();
    setDraft((prev) => arrayMove(prev, fromIndex, toIndex));
  };

  const handleAddAndSave = () => {
    if (!canAdd) return;
    clearSaveError();
    replaceAll([...draft, { label: trimmed, pathname: page.pathname, url: page.url }]);
  };

  const handleAddKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddAndSave();
    }
  };

  const handleSave = () => {
    replaceAll(draft);
  };

  const renderOverlay = (id: string) => {
    const favorite = draft.find((candidate) => candidate.pathname === id);
    if (!favorite) return null;
    return <FavoriteManagerRow favorite={favorite} onRename={() => {}} onRemove={() => {}} />;
  };

  return (
    <ModalComposable
      open={open}
      onClose={onClose}
      size="default"
      dataTest={TestIds.FAVORITES_MANAGER}
      aria-label="Favorites Manager"
    >
      <ModalComposable.Header
        title="Favorites Manager"
        subtitle={
          <>
            {`Favorite up to ${FAVORITES_CAP} pages and reach them instantly from your sidebar — just click + Favorite Current Page on any page.`}
            {atCap && (
              <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: capitalDesignTokens.foundations.colors.gray100,
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <StarOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Box component="span" sx={{ typography: 'caption', color: 'text.secondary' }}>
                    {`${draft.length} of ${FAVORITES_CAP} favorites · Edit to make room`}
                  </Box>
                </Box>
              </Box>
            )}
          </>
        }
      />

      {/* ModalContent ships with `padding: 0`; supply our own. px:3 aligns with the footer
          inset. Top spacing lives on the inner Stack because MUI's `.MuiDialogTitle-root + &`
          rule forces the content's padding-top to 0 with higher specificity than sx. */}
      <ModalComposable.Content sx={{ px: 3, pb: 2 }}>
        <Stack gap={3} sx={{ pt: 2 }}>
          {isSaveError && (
            <Alert variant="condensed" severity="error" sx={{ alignItems: 'center' }}>
              Failed to save favorites. Please try again.
            </Alert>
          )}
          {/* Current favorites */}
          <Stack gap={1} data-testid={TestIds.FAVORITES_CURRENT_SECTION}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h4" fontWeight={600}>
                Current favorites
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${draft.length}/${FAVORITES_CAP}`}
              </Typography>
            </Stack>
            {draft.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No favorites yet. Add the current page below.
              </Typography>
            ) : (
              <SortableList ids={ids} onReorder={handleReorder} renderOverlay={renderOverlay}>
                <List disablePadding>
                  {draft.map((favorite) => (
                    <SortableItem key={favorite.pathname} id={favorite.pathname}>
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <FavoriteManagerRow
                          favorite={favorite}
                          onRename={handleRename}
                          onRemove={handleRemove}
                          setNodeRef={setNodeRef}
                          style={style}
                          dragHandleAttributes={attributes}
                          dragHandleListeners={listeners}
                        />
                      )}
                    </SortableItem>
                  ))}
                </List>
              </SortableList>
            )}
          </Stack>

          {isAdding ? (
            <Stack gap={1.5} data-testid={TestIds.FAVORITES_ADD_SECTION}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h4" fontWeight={600}>
                  Favorite current page
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Cancel adding favorite"
                  data-testid={TestIds.FAVORITES_ADD_DISMISS_BUTTON}
                  onClick={() => {
                    setIsAdding(false);
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
              <TextField
                label="Title"
                value={label}
                onChange={(event) => {
                  setLabel(event.target.value);
                }}
                onKeyDown={handleAddKeyDown}
                fullWidth
                size="small"
                autoFocus
              />
              <TextField
                label="URL"
                value={destinationUrl}
                fullWidth
                size="small"
                disabled
                slotProps={{ input: { readOnly: true } }}
              />
            </Stack>
          ) : (
            <Stack gap={1} data-testid={TestIds.FAVORITES_ADD_SECTION}>
              <Box>
                <Button
                  variant="text"
                  startIcon={<StarOutlineIcon sx={{ fontSize: 20 }} />}
                  data-testid={TestIds.FAVORITES_ADD_CURRENT_PAGE_BUTTON}
                  disabled={atCap || alreadySaved}
                  onClick={() => {
                    setLabel(document.title);
                    setIsAdding(true);
                  }}
                >
                  Favorite current page
                </Button>
                {!atCap && alreadySaved && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    This page is already in your favorites.
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </Stack>
      </ModalComposable.Content>

      <ModalComposable.Actions>
        <Box />
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            color="secondary"
            data-testid={TestIds.FAVORITES_CLOSE_BUTTON}
            onClick={onClose}
          >
            Close
          </Button>
          {isAdding && canAdd ? (
            <Button
              variant="contained"
              data-testid={TestIds.FAVORITES_ADD_SAVE_BUTTON}
              onClick={handleAddAndSave}
              loading={isSaving}
              loadingIndicator={
                <CircularProgress color="inherit" {...circularProgressSizeProps.extraSmall} />
              }
            >
              <span>{isDirty ? 'Add and Save' : 'Add'}</span>
            </Button>
          ) : isDirty ? (
            <Button
              variant="contained"
              data-testid={TestIds.FAVORITES_SAVE_BUTTON}
              onClick={handleSave}
              loading={isSaving}
              loadingIndicator={
                <CircularProgress color="inherit" {...circularProgressSizeProps.extraSmall} />
              }
            >
              <span>Save</span>
            </Button>
          ) : null}
        </Stack>
      </ModalComposable.Actions>
    </ModalComposable>
  );
}
